/**
 * Error Boundary Component
 * Catches errors in child components and displays a fallback UI
 *
 * Firestore assertion errors are auto-recovered: the component resets its error
 * state and reloads once (tracked via sessionStorage to prevent infinite loops).
 */

import { Component, ReactNode } from 'react';
import { ErrorState } from './ui/ErrorState';
import { resetFirestoreCache } from '../config/firebase';

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
}

interface State {
  hasError: boolean;
  error?: Error;
  errorInfo?: React.ErrorInfo;
  isClearing: boolean;
}

const RELOAD_KEY = 'lucidDock_firestoreAutoReload';
const RELOAD_COOLDOWN_MS = 10_000;

function isFirestoreCacheError(error?: Error): boolean {
  if (!error?.message) return false;
  const message = error.message.toLowerCase();
  return (
    message.includes('firestore') &&
    (message.includes('internal assertion failed') ||
      message.includes('unexpected state') ||
      message.includes('target already exists'))
  );
}

async function clearFirestoreCache(): Promise<void> {
  await resetFirestoreCache();

  const keysToRemove: string[] = [];
  for (let i = 0; i < localStorage.length; i++) {
    const key = localStorage.key(i);
    if (key) {
      const isFirestoreCache = key.includes('firestore') && !key.includes('Auth');
      if (isFirestoreCache) {
        keysToRemove.push(key);
      }
    }
  }
  keysToRemove.forEach((key) => {
    localStorage.removeItem(key);
  });
}

/**
 * Install a global handler that catches Firestore assertion errors thrown
 * outside React's lifecycle (e.g. from PersistentListenStream.onNext).
 * These errors are unrecoverable in the current SDK connection, so we
 * suppress them to prevent the browser from showing them as uncaught.
 * The onSnapshot error callbacks and ErrorBoundary handle recovery.
 */
function installGlobalFirestoreErrorHandler(): void {
  if (typeof window === 'undefined') return;
  // Only install once
  if ((window as any).__firestoreErrorHandlerInstalled) return;
  (window as any).__firestoreErrorHandlerInstalled = true;

  window.addEventListener('error', (event) => {
    const msg = event.error?.message || event.message || '';
    if (
      msg.includes('INTERNAL ASSERTION FAILED') ||
      msg.includes('Unexpected state')
    ) {
      // Suppress the uncaught error - it's handled by onSnapshot error callbacks
      event.preventDefault();
    }
  });

  window.addEventListener('unhandledrejection', (event) => {
    const msg = event.reason?.message || String(event.reason || '');
    if (
      msg.includes('INTERNAL ASSERTION FAILED') ||
      msg.includes('Unexpected state')
    ) {
      event.preventDefault();
    }
  });
}

// Install on module load
installGlobalFirestoreErrorHandler();

export class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false, isClearing: false };
  }

  static getDerivedStateFromError(error: Error): Partial<State> {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    console.error('Error caught by boundary:', error, errorInfo);
    this.setState({ errorInfo });

    // For Firestore assertion errors, attempt one auto-recovery reload
    if (isFirestoreCacheError(error)) {
      const lastReload = Number(sessionStorage.getItem(RELOAD_KEY) || '0');
      const now = Date.now();

      if (now - lastReload > RELOAD_COOLDOWN_MS) {
        sessionStorage.setItem(RELOAD_KEY, String(now));
        // Clear cache and reload
        clearFirestoreCache()
          .catch(() => {})
          .finally(() => {
            window.location.reload();
          });
        return;
      }
      // If we already auto-reloaded recently, fall through to show the error UI
    }
  }

  handleRetry = () => {
    this.setState({ hasError: false, error: undefined, errorInfo: undefined });
    window.location.reload();
  };

  handleClearCacheAndReload = async () => {
    this.setState({ isClearing: true });
    try {
      await clearFirestoreCache();
      await new Promise((resolve) => setTimeout(resolve, 500));
      // Reset the cooldown so auto-recovery works again after manual clear
      sessionStorage.removeItem(RELOAD_KEY);
      window.location.reload();
    } catch (err) {
      console.error('Failed to clear cache:', err);
      window.location.reload();
    }
  };

  render() {
    if (this.state.hasError) {
      if (this.props.fallback) {
        return this.props.fallback;
      }

      const isFirestoreError = isFirestoreCacheError(this.state.error);

      if (isFirestoreError) {
        return (
          <ErrorState
            title="Cache Sync Error"
            message={
              this.state.isClearing
                ? 'Clearing cache and refreshing...'
                : 'A database sync error occurred. Click retry to clear the cache and refresh.'
            }
            onRetry={this.handleClearCacheAndReload}
            type="generic"
          />
        );
      }

      return (
        <ErrorState
          title="Something Went Wrong"
          message={this.state.error?.message || 'An unexpected error occurred. Please try refreshing the page.'}
          onRetry={this.handleRetry}
          type="generic"
        />
      );
    }

    return this.props.children;
  }
}
